import axios from 'axios';
import {
  getErrorMessageTimeout,
  getFromLocalStorage,
  setToLocalStorage,
} from '../../global/helpers';
import {
  AddLoyaltyProductPayload,
  GetHealthStatusPayload,
  SendToStandartPayload,
} from './types';

export class LoyaltyService {
  static API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1/`;

  // Лаконичный хелпер, чтобы дублировать системный показ ErrorAlertModal и для fetch-запросов.
  private static pushErrorToModal({
    url,
    status,
    errorMessage,
    code = null,
    body = null,
    xRequestId = '',
    xTraceId = '',
  }: {
    url: string;
    status: number | string;
    errorMessage: string;
    code?: string | null;
    body?: any;
    xRequestId?: string;
    xTraceId?: string;
  }) {
    setToLocalStorage('requestErrorData', {
      url,
      status,
      error_message: errorMessage,
      code,
      body,
      token: getFromLocalStorage('accessToken'),
      time: getErrorMessageTimeout(),
      x_trace_id: xTraceId,
      x_request_id: xRequestId,
    });
    window.dispatchEvent(new Event('storage'));
  }

  // Унифицированный обработчик axios-ошибок для всех эндпоинтов loyalty.
  private static handleAxiosError(error: any, fallbackUrl: string) {
    const status = error?.response?.status || '';
    const data = error?.response?.data;
    const url =
      error?.request?.responseURL || error?.config?.url || fallbackUrl;
    const errorMessage =
      data?.error ||
      data?.message ||
      data?.detail ||
      error?.message ||
      'Network Error';
    const code = data?.code || null;
    const xRequestId = error?.response?.headers?.['x-request-id'] || '';
    const xTraceId = error?.response?.headers?.['x-trace-id'] || '';

    LoyaltyService.pushErrorToModal({
      url,
      status,
      errorMessage,
      code,
      body: data || null,
      xRequestId,
      xTraceId,
    });
  }

  static getCurrentReceipt = async () => {
    const url = `${LoyaltyService.API_BASE_URL}orders/soft/products`;
    try {
      const response = await axios({
        method: 'get',
        url,
        // timeout: 4500,
      });
      return response.data;
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  static getCurrentPharmacist = async () => {
    const url = `${LoyaltyService.API_BASE_URL}loyalty/ph/balance`;
    try {
      const response = await axios({
        method: 'get',
        url,
        headers: {
          Authorization: `Bearer ${getFromLocalStorage('accessToken')}`,
        },
      });
      return response.data;
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  static getPharmacistTodayBonuses = async () => {
    const url = `${LoyaltyService.API_BASE_URL}loyalty/ph/bonuses/today`;
    try {
      const response = await axios({
        method: 'get',
        url,
        headers: {
          Authorization: `Bearer ${getFromLocalStorage('accessToken')}`,
        },
      });
      return response.data;
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  static getClientInfo = async (payload: { phone?: string; code?: string }) => {
    const { phone, code } = payload;
    const url = `${LoyaltyService.API_BASE_URL}loyalty/balance`;

    const data = phone ? { phone } : { code };
    try {
      const response = await axios({
        method: 'post',
        url,
        headers: {
          Authorization: `Bearer ${getFromLocalStorage('accessToken')}`,
        },
        data,
      });
      return response.data;
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  static getClientOrders = async (payload: { phone: string }) => {
    const url = `${LoyaltyService.API_BASE_URL}orders/active/partner`;

    try {
      const response = await axios({
        method: 'post',
        url,
        headers: {
          Authorization: `Bearer ${getFromLocalStorage('accessToken')}`,
        },
        data: payload,
      });
      return response.data;
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  static applyBonuses = async (phone: string) => {
    const url = `${LoyaltyService.API_BASE_URL}auth/loyalty`;
    try {
      const response = await axios({
        method: 'post',
        url,
        data: {
          phone,
        },
      });
      return response.data;
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  static submitLoyaltyOrder = async (payload: any) => {
    const { isPartner, ...body } = payload;

    const url = `${LoyaltyService.API_BASE_URL}orders/confirm${
      isPartner ? '/partner' : ''
    }`;
    try {
      const response = await axios({
        method: 'post',
        url,
        data: body,
      });
      return {
        ...response.data,
        docId: payload.doc_id,
        bonusUsed: payload.bonus_used,
      };
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  static setPartnerPaymentMethod = async (orderId: string) => {
    const url = `${LoyaltyService.API_BASE_URL}orders/${orderId}/payments/change/partner`;
    try {
      const response = await axios({
        method: 'post',
        url,
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          order_id: orderId,
        },
      });

      return response.data;
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  static setInPlacePaymentMethod = async (orderId: string) => {
    const url = `${LoyaltyService.API_BASE_URL}orders/${orderId}/payments/change/partner`;
    try {
      const response = await axios({
        method: 'put',
        url,
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          order_id: orderId,
        },
      });

      return response.data;
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  static sendToStandart = async (payload: SendToStandartPayload) => {
    const url = `http://127.0.0.1:8000/scan`;
    try {
      const response = await axios({
        method: 'post',
        url,
        data: payload,
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  static getHealthStatus = async (payload: GetHealthStatusPayload) => {
    const url = `http://127.0.0.1:8000/health?source_code=${payload.source_code}`;
    try {
      const response = await axios({
        method: 'get',
        url,
        headers: {
          Authorization: `Bearer ${getFromLocalStorage('accessToken')}`,
        },
      });
      return response.data;
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  static generateLoyaltyReport = async (
    data: {
      from: string;
      to: string;
      integration_code: string;
      type: string;
      source_code?: string;
      phone?: string;
    },
    pdfF: boolean,
    xls: boolean
  ) => {
    const endpoint = `${LoyaltyService.API_BASE_URL}reports/loyalty/${
      pdfF ? 'pdf' : 'xls'
    }`;
    const fileName = data.source_code
      ? `${data.source_code}/${Math.round(Date.now() / 1000)}`
      : `${Math.round(Date.now() / 1000)}`;
    try {
      const response = await axios({
        method: 'post',
        url: endpoint,
        data,
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${getFromLocalStorage('accessToken')}`,
        },
      });

      const blob = response.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      return {};
    } catch (err) {
      LoyaltyService.handleAxiosError(err, endpoint);
      window.alert('Error');
      throw err;
    }
  };

  static addLoyaltyProduct = async (payload: AddLoyaltyProductPayload) => {
    const url = `${LoyaltyService.API_BASE_URL}loyalty/products`;
    try {
      const response = await axios({
        method: 'post',
        url,
        data: payload,
        headers: {
          Authorization: `Bearer ${getFromLocalStorage('accessToken')}`,
        },
      });
      return response.data;
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  static getLoyaltyProducts = async () => {
    const url = `${LoyaltyService.API_BASE_URL}loyalty/products`;
    try {
      const response = await axios({
        method: 'get',
        url,
        headers: {
          Authorization: `Bearer ${getFromLocalStorage('accessToken')}`,
        },
      });
      return response.data;
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  static deleteLoyaltyProduct = async (payload: { sku: string }) => {
    const url = `${LoyaltyService.API_BASE_URL}loyalty/products`;
    try {
      const response = await axios({
        method: 'delete',
        url,
        data: payload,
        headers: {
          Authorization: `Bearer ${getFromLocalStorage('accessToken')}`,
        },
      });
      return response.data;
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  static getLoyaltyPercentages = async () => {
    const url = `${LoyaltyService.API_BASE_URL}loyalty/percents`;
    try {
      const response = await axios({
        method: 'get',
        url,
        headers: {
          Authorization: `Bearer ${getFromLocalStorage('accessToken')}`,
        },
      });
      return response.data;
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  // Получение рекомендаций для программы лояльности
  static getRecommendations = async (payload: {
    user_hash: string;
    source_code: string;
    user_cart: Array<{
      ware_id: string;
      name: string;
      quantity: number;
    }>;
  }) => {
    const url =
      'https://loyaltyrecapi-production.up.railway.app/loyalty_program_recommendations';
    try {
      const response = await axios({
        method: 'post',
        url,
        data: payload,
      });
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  // Начисление дополнительных бонусов клиенту менеджером.
  static creditBonuses = async (payload: {
    user_phone: string;
    source_code: string;
    reason: string;
    amount: number;
  }) => {
    const url = `${LoyaltyService.API_BASE_URL}loyalty/credit`;
    try {
      const response = await axios({
        method: 'post',
        url,
        data: payload,
        headers: {
          Authorization: `Bearer ${getFromLocalStorage('accessToken')}`,
        },
      });
      return response.data;
    } catch (err) {
      LoyaltyService.handleAxiosError(err, url);
      throw err;
    }
  };

  static setLoyaltyInfoBot = async (
    sourceCode: string,
    action: string,
    networkName: string,
    dockId: string
  ) => {
    const TOKEN = process.env.REACT_APP_LOYALTY_TG_BOT_TOKEN;
    const CHAT_ID = process.env.REACT_APP_LOYALTY_TG_BOT_CHAT_ID;
    const API_URL = `https://api.telegram.org/bot${TOKEN}`;

    if (!TOKEN || !CHAT_ID || process.env.REACT_APP_DEV_AREA) {
      return;
    }
    const res = await fetch(`${API_URL}/sendMessage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: CHAT_ID,
        text: `Аптека: ${sourceCode} \n${networkName} \nЧек (в стандарте): ${dockId} \nСобытие: ${action}`,
        parse_mode: 'HTML',
      }),
    });
    const data = await res.json();
    if (!data.ok) throw new Error(data.description);
    return data.result;
  };
}
